import { State } from '@meraki-internal/state';
import { APIClient } from '@innerhive-internal/innerhive-api-client';
import { BeeChatClient, IChat, IChatMessage, ICareMapKey } from './BeeChatClient';
import { CareMapState } from '../caremap/CareMapState';
import { AppPrincipal } from '../auth/AppPrincipal';

export const BEA_WELCOME_MESSAGE = `Hi, I'm Bea! I'm here to help with care questions, resources, or just to listen. How can I help?`;

export type BeeChatView = 'chat' | 'list';

interface IBeeChatStateData {
    chats: IChat[];
    activeChat: IChat | null;
    messages: IChatMessage[];
    isSending: boolean;
    currentView: BeeChatView;
}

export class BeeChatState extends State<IBeeChatStateData> {
    static inject = () => [
        BeeChatClient,
        APIClient,
        CareMapState,
        AppPrincipal
    ];

    constructor(
        private chatClient: BeeChatClient,
        private apiClient: APIClient,
        private careMapState: CareMapState,
        private principal: AppPrincipal
    ) {
        super({
            chats: [],
            activeChat: null,
            messages: [],
            isSending: false,
            currentView: 'chat'
        });
    }

    private ensureWelcomeMessage = (messages: IChatMessage[]): IChatMessage[] => {
        // Check if first message is already the welcome message
        if (messages.length > 0 && messages[0].role === 'assistant' && messages[0].content === BEA_WELCOME_MESSAGE) {
            return messages;
        }

        // Prepend welcome message
        const welcomeMessage: IChatMessage = {
            role: 'assistant',
            content: BEA_WELCOME_MESSAGE,
            createdAt: messages.length > 0 ? messages[0].createdAt : new Date().toISOString()
        };
        return [welcomeMessage, ...messages];
    };

    private getCareMapKey = (): ICareMapKey | undefined => {
        const careMap = this.careMapState.getMostRecentCaremap();
        if (!careMap) {
            return undefined;
        }
        return {
            userId: careMap.userId,
            careMapId: careMap.careMapId
        };
    };

    loadChats = async () => {
        const entry = await this.apiClient.entry();
        if (!entry.links.chats) {
            return;
        }
        const chats = await this.chatClient.getChats(entry.links.chats.href);
        this.setState({ chats });
    };

    startNewConversation = () => {
        const welcomeMessage: IChatMessage = {
            role: 'assistant',
            content: BEA_WELCOME_MESSAGE,
            createdAt: new Date().toISOString()
        };

        this.setState({
            activeChat: null,
            messages: [welcomeMessage],
            currentView: 'chat'
        });
    };

    selectChat = (chatId: string) => {
        const chat = this.state.chats.find(c => c.chatId === chatId);
        if (chat) {
            // First update the messages while still on list view
            this.setState({
                activeChat: chat,
                messages: this.ensureWelcomeMessage(chat.messages)
            });
            // Delay allows React render and browser paint to complete before animating
            setTimeout(() => {
                this.setState({ currentView: 'chat' });
            }, 150);
        }
    };

    showConversationList = () => {
        this.setState({ currentView: 'list' });
    };

    showChat = () => {
        this.setState({ currentView: 'chat' });
    };

    sendMessage = async (text: string) => {
        const userMessage: IChatMessage = {
            role: 'user',
            content: text,
            createdAt: new Date().toISOString()
        };

        // Add user message to UI immediately
        const updatedMessages = [...this.state.messages, userMessage];
        this.setState({
            messages: updatedMessages,
            isSending: true
        });

        try {
            const entry = await this.apiClient.entry();
            const isNewChat = !this.state.activeChat;
            const careMapKey = this.getCareMapKey();

            let chat: IChat;
            if (isNewChat) {
                // Start a new chat
                chat = await this.chatClient.startNewChat({
                    chatsUrl: entry.links.chats.href,
                    message: text,
                    careMapKey
                });
            } else {
                // Continue existing chat
                chat = await this.chatClient.continueChat({
                    chat: this.state.activeChat!,
                    message: text,
                    careMapKey
                });
            }

            // Update state with the returned chat (includes full message history)
            let updatedChats: IChat[];
            if (isNewChat) {
                updatedChats = [chat, ...this.state.chats];
            } else {
                updatedChats = this.state.chats.map(c =>
                    c.chatId === chat.chatId ? chat : c
                );
            }

            this.setState({
                activeChat: chat,
                messages: this.ensureWelcomeMessage(chat.messages),
                chats: updatedChats,
                isSending: false
            });
        } catch (error) {
            console.error('Error sending message to Bea:', error);

            const errorMessage: IChatMessage = {
                role: 'assistant',
                content: 'I apologize, but I encountered an error. Please try again.',
                createdAt: new Date().toISOString()
            };

            this.setState({
                messages: [...updatedMessages, errorMessage],
                isSending: false
            });
        }
    };

    getSummary = (chat: IChat): string => {
        // Get the most recent message
        if (chat.messages.length > 0) {
            return chat.messages[chat.messages.length - 1].content;
        }
        return 'New conversation';
    };

    hasChats = (): boolean => {
        return this.state.chats.length > 0;
    };
}
